import { ArrowLeftIcon, ArrowRightIcon, ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  HStack,
  IconButton,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Select,
  Skeleton,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
} from '@chakra-ui/react';
import ToggleTab from 'components/ToggleTab';
import cuid from 'cuid';
import { range } from 'lodash';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAsyncDebounce, useFilters, useGlobalFilter, useTable } from 'react-table';

import { DefaultFilterForColumn, GlobalFilter } from './Filter';

// fall back data for columns and data is defined outside the scope where `useReactTable` is called providing stable reference
const fallbackData: Array<any> = [];

export const DataTable = ({
  autoFocus,
  setFocusInput,
  columns = [],
  currentPage,
  customGlobalFilter,
  data,
  globalFilter,
  globalFilterPlaceholder,
  hasSearchInput,
  hasFilters,
  isMoreThenOnePage,
  loading,
  perPage,
  isMultiCheckbox,
  resetFilter,
  setGlobalFilter,
  setPage,
  setPerPage,
  total,
  filtersSelected,
  filtersInitiallyOpen: _filtersInitiallyOpen,
  ...otherProps
}: {
  autoFocus?: boolean;
  setFocusInput?: Dispatch<SetStateAction<boolean>>;
  columns: Array<any>;
  currentPage?: number;
  customGlobalFilter?: Element;
  data: any;
  globalFilter?: string;
  globalFilterPlaceholder?: string;
  hasSearchInput?: boolean;
  isMoreThenOnePage: boolean;
  hasFilters?: boolean;
  loading?: boolean;
  perPage?: number;
  isMultiCheckbox?: boolean;
  resetFilter?(): void;
  setGlobalFilter?: Dispatch<any>;
  setPage?(val: number): void;
  setPerPage?(val: number): void;
  total?: number;
  filtersSelected?: boolean;
  filtersInitiallyOpen?: boolean;
}) => {
  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, rows } = useTable(
    {
      columns: columns ?? fallbackData,
      data: data ?? fallbackData,
      // @ts-ignore
      defaultColumn: { Filter: DefaultFilterForColumn },
    },
    useFilters,
    useGlobalFilter,
  );
  const { t } = useTranslation();
  const pageOptions = range(0, total, 1);

  const [searchString, setSearchString] = useState(globalFilter);

  useEffect(() => {
    setSearchString(globalFilter);
  }, [globalFilter]);

  const inputOnChange = useAsyncDebounce(() => {
    if (setGlobalFilter) {
      setGlobalFilter(searchString || undefined);
    }
  }, 2500);

  const debouncedSetPage = useAsyncDebounce((page) => {
    if (setPage) {
      setPage(page);
    }
  }, 1250);

  return (
    <Stack direction={'column'} flex={1}>
      {/* @ts-ignore*/}
      <Grid
        templateColumns={{
          base: '1fr',
          xl: 'minmax(auto,15rem) minmax(auto,15rem)  1fr 2fr',
        }}
        templateAreas={{ base: '', xl: `"filters filters . global"` }}
        gap={2}
      >
        {hasSearchInput && (
          <GridItem area={{ base: '', xl: 'global' }}>
            <GlobalFilter
              // @ts-ignore
              globalFilterPlaceholder={globalFilterPlaceholder}
              // @ts-ignore
              preGlobalFilteredRows={data}
              globalFilter={globalFilter}
              // @ts-ignore
              setGlobalFilter={setGlobalFilter}
              key={'search-' + cuid()}
              autoFocus={autoFocus}
              setFocusInput={setFocusInput}
              searchString={searchString}
              setSearchString={setSearchString}
              inputOnChange={inputOnChange}
            />
          </GridItem>
        )}

        {customGlobalFilter && (
          <GridItem area={{ base: '', xl: 'filters' }}>
            <Grid templateColumns={{ base: '1fr', xl: '1fr 1fr' }} gap={2}>
              {/* @ts-ignore*/}
              {customGlobalFilter}
            </Grid>
          </GridItem>
        )}
      </Grid>

      {(hasSearchInput || hasFilters) && !isMultiCheckbox && (
        <Box display={'flex'} justifyContent={'flex-end'} pt={15} pb={hasFilters ? 0 : 10}>
          <Button onClick={resetFilter} width={150} ml={'auto'}>
            {t('components:table.filters.reset')}
          </Button>
        </Box>
      )}
      {hasFilters && (
        <Grid gap={4} paddingBlock={4} paddingInline={4} mr={3}>
          <HStack justifyContent={'space-between'}>
            <ToggleTab
              filtersSelected={filtersSelected}
              testId="table-filter-toggle"
              // openCaption={t('components:table.filters.open')}
              openCaption={
                filtersSelected
                  ? ((
                      <HStack>
                        <Text>{t('components:table.filters.active')}</Text>
                        <Text>{t('components:table.filters.open')}</Text>
                      </HStack>
                    ) as unknown as string)
                  : t('components:table.filters.open')
              }
              closeCaption={t('components:table.filters.close')}
            >
              <Grid templateColumns={{ base: '1fr', lg: '1fr auto' }} alignItems={'center'} columnGap={2} rowGap={4}>
                <Box>
                  {headerGroups.map((headerGroup: { getHeaderGroupProps: () => any; headers: any[] }, headerKey: number) => {
                    return (
                      <Flex key={headerKey} flexWrap={'wrap'}>
                        {headerGroup.headers
                          .filter((column) => column?.shouldFilter)
                          .map((column, headerChildKey) => {
                            return (
                              <Box key={headerChildKey} mr={3}>
                                {column.shouldFilter ? <>{column.render('Filter')}</> : null}
                              </Box>
                            );
                          })}
                      </Flex>
                    );
                  })}
                </Box>
              </Grid>
              {isMultiCheckbox && (
                <Box display={'flex'} justifyContent={'flex-end'} pt={30} pb={hasFilters ? 0 : 10}>
                  <Button onClick={resetFilter} width={150} ml={'auto'}>
                    {t('components:table.filters.reset')}
                  </Button>
                </Box>
              )}
            </ToggleTab>
          </HStack>
        </Grid>
      )}

      <Box maxWidth={'100%'} overflow={'auto'}>
        <Table {...getTableProps()} {...otherProps}>
          <Thead>
            {headerGroups.map((headerGroup: { getHeaderGroupProps: () => any; headers: any[] }, headerKey: number) => (
              <Tr {...headerGroup.getHeaderGroupProps()} key={headerKey}>
                {headerGroup.headers.map((column, headerChildKey) => (
                  <Th {...column.getHeaderProps()} key={headerChildKey}>
                    {column.render('Header')}
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>
          {loading && (
            <Tbody {...getTableBodyProps()}>
              {Array.from({ length: 10 }).map(() => {
                return headerGroups.map((headerGroup, skeletonHeaderKey) => (
                  <Tr {...headerGroup.getHeaderGroupProps()} key={skeletonHeaderKey}>
                    {headerGroup.headers.map((column, skeletonColumnKey) => (
                      <Td {...column.getHeaderProps()} key={skeletonColumnKey}>
                        <Skeleton height="20px" speed={2} fadeDuration={8} />
                      </Td>
                    ))}
                  </Tr>
                ));
              })}
            </Tbody>
          )}

          {!loading &&
            (data?.length === 0 ? (
              <Tbody data-test-id="no-matches">
                <Tr>
                  {/* @ts-ignore*/}
                  <Td colSpan="100%">{t('components:table.no_matches')} </Td>
                </Tr>
              </Tbody>
            ) : (
              <Tbody {...getTableBodyProps()}>
                {rows.map(
                  (
                    row: {
                      getRowProps: () => any;
                      cells: any[];
                    },
                    i: any,
                  ) => {
                    // @ts-ignore
                    prepareRow(row);
                    return (
                      <Tr {...row.getRowProps()} key={i}>
                        {row.cells.map((cell, cellKey) => {
                          return (
                            <Td {...cell.getCellProps()} key={cellKey} backgroundColor={cell.row.original.highlighted ? 'orange.100' : undefined}>
                              {cell.render('Cell')}
                            </Td>
                          );
                        })}
                      </Tr>
                    );
                  },
                )}
              </Tbody>
            ))}
        </Table>
      </Box>

      {isMoreThenOnePage && !loading && (
        <Flex justifyContent="space-between" m={4} alignItems="center">
          <Flex>
            <Tooltip label={t('components:table.first')}>
              <IconButton
                // @ts-ignore
                onClick={() => setPage(1)}
                isDisabled={currentPage === 0}
                icon={<ArrowLeftIcon h={3} w={3} />}
                mr={4}
                data-test-id="table-first"
                aria-label={'first'}
              />
            </Tooltip>
            <Tooltip label={t('components:table.previous')}>
              <IconButton
                // @ts-ignore
                onClick={() => setPage(currentPage)}
                isDisabled={currentPage === 0}
                icon={<ChevronLeftIcon h={6} w={6} />}
                data-test-id="table-previous"
                aria-label={'previous'}
              />
            </Tooltip>
          </Flex>

          <Flex alignItems="center">
            <Text mr={8}>
              {t('components:table.page')}
              <Text fontWeight="bold" as="span">
                {
                  // @ts-ignore
                  currentPage + 1
                }
              </Text>{' '}
              {t('components:table.of')}
              <Text fontWeight="bold" as="span">
                {pageOptions.length}
              </Text>
            </Text>
            <Text> {t('components:table.go_to_page')}</Text>{' '}
            <NumberInput
              ml={2}
              mr={8}
              w={28}
              min={1}
              max={pageOptions.length}
              data-test-id="table-go-to"
              onChange={(value) => {
                const page = value ? (value as any) - 1 : 0;
                debouncedSetPage(page + 1);
              }}
              defaultValue={(currentPage as any) + 1}
            >
              <NumberInputField />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
            <Select
              w={48}
              value={perPage}
              data-test-id="table-show-items"
              onChange={(e) => {
                // @ts-ignore
                setPerPage(Number(e.target.value));
              }}
            >
              {[10, 20, 30, 40, 50].map((pageSize) => (
                <option key={pageSize} value={pageSize}>
                  {t('components:table.show')} {pageSize}
                </option>
              ))}
            </Select>
          </Flex>

          <Flex>
            <Tooltip label={t('components:table.next')}>
              <IconButton
                // @ts-ignore
                onClick={() => setPage(currentPage + 2)}
                // @ts-ignore
                isDisabled={currentPage + 1 === total}
                icon={<ChevronRightIcon h={6} w={6} />}
                data-test-id="table-next"
                aria-label={'next'}
              />
            </Tooltip>
            <Tooltip label={t('components:table.last')}>
              <IconButton
                // @ts-ignore
                onClick={() => setPage(total)}
                // @ts-ignore
                isDisabled={currentPage + 1 === total}
                icon={<ArrowRightIcon h={3} w={3} />}
                ml={4}
                data-test-id="table-last"
                aria-label={'last'}
              />
            </Tooltip>
          </Flex>
        </Flex>
      )}
    </Stack>
  );
};
